/**
 * "Network SLA Summary" report - view past SLA NYR units aggregated by temp zone
 */

import React, { useRef, useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import Column from "@amzn/meridian/column"
import Heading from "@amzn/meridian/heading"
import Row from "@amzn/meridian/row"

import PageLayout from "../../components/app/page-layout"
import NetworkSLATempSummaryTable from "../../components/network-sla-temp-summary/network-sla-temp-summary-table"
import TableFooter from "../../components/app/table-footer"
import usePagination from "../../hooks/use-pagination"
import { getSummaryQuantity } from "../../utilities/parent-child-conversion"
import { slaShipmentProgressThreshold } from "../../components/Constants"
const NetworkSLATempSummary = props => {
    const { incomingDockReceived, warehouseParams } = props
    const [resultsPerPage, setResultsPerPage] = useState(5)
    const [selectedResultsPerPage, setSelectedResultsPerPage] = useState(5)
    const tableWrapperRef = useRef()

    // Memoized aggregate function to avoid recalculation on every render
    const summaryData = useMemo(() => {
        // Aggregate data by temp zone, grouped by warehouse like Expected Incoming Shipments
        const aggregateByTempZone = (shipments) => {
            const warehouseData = {}

            if (!shipments) return []

            shipments.forEach(shipment => {
                const tempZone = shipment.tempZone || 'UNKNOWN'
                const warehouseId = shipment.warehouseId

                // Find warehouse info for site ID
                const warehouseInfo = warehouseParams?.find(w => w.warehouse_id === warehouseId)
                const siteId = warehouseInfo?.warehouse_id || warehouseId

                if (!warehouseData[siteId]) {
                    warehouseData[siteId] = {
                        warehouseId: siteId,
                        tempZones: {}
                    }
                }

                if (!warehouseData[siteId].tempZones[tempZone]) {
                    warehouseData[siteId].tempZones[tempZone] = {
                        totalExpected: 0,
                        totalReceived: 0,
                        totalNYR: 0,
                        totalNYRPastSLA: 0,
                        percentReceived: 0
                    }
                }

                const expectedUnits = Math.max(getSummaryQuantity(shipment, 'confirmedUnits', 'childConfirmedUnits'), 0)
                const receivedUnits = Math.max(getSummaryQuantity(shipment, 'receivedUnits', 'childReceivedUnits'), 0)

                // Calculate NYR units past SLA using the same logic as dock received view
                let nyrPastSLA = 0
                if (shipment.progress < slaShipmentProgressThreshold) {
                    const slaDeadline = shipment.lastDockReceiveTime +
                        ((shipment.sla ? Math.min(shipment.sla, 24) : 24) * 60 * 60 * 1000)

                    if (Date.now() > slaDeadline) {
                        // Only count NYR units for shipments that are past SLA
                        nyrPastSLA = Math.max(expectedUnits - receivedUnits, 0)
                    }
                }

                warehouseData[siteId].tempZones[tempZone].totalExpected += expectedUnits
                warehouseData[siteId].tempZones[tempZone].totalReceived += receivedUnits
                warehouseData[siteId].tempZones[tempZone].totalNYRPastSLA += nyrPastSLA
            })

            // Calculate NYR and percentage for each temp zone and site totals
            Object.values(warehouseData).forEach(warehouse => {
                let siteTotalExpected = 0
                let siteTotalReceived = 0
                let siteTotalNYRPastSLA = 0

                Object.values(warehouse.tempZones).forEach(tempZoneData => {
                    tempZoneData.totalNYR = Math.max(tempZoneData.totalExpected - tempZoneData.totalReceived, 0)
                    tempZoneData.percentReceived = tempZoneData.totalExpected > 0
                        ? ((tempZoneData.totalReceived / tempZoneData.totalExpected) * 100).toFixed(1)
                        : 0

                    // Calculate percentage of NYR that is past SLA
                    tempZoneData.percentPastSLA = tempZoneData.totalNYR > 0
                        ? ((tempZoneData.totalNYRPastSLA / tempZoneData.totalNYR) * 100).toFixed(1)
                        : 0

                    // Accumulate site totals
                    siteTotalExpected += tempZoneData.totalExpected
                    siteTotalReceived += tempZoneData.totalReceived
                    siteTotalNYRPastSLA += tempZoneData.totalNYRPastSLA
                })

                // Calculate site-level totals
                const siteTotalNYR = Math.max(siteTotalExpected - siteTotalReceived, 0)
                warehouse.siteTotal = {
                    totalExpected: siteTotalExpected,
                    totalReceived: siteTotalReceived,
                    totalNYR: siteTotalNYR,
                    totalNYRPastSLA: siteTotalNYRPastSLA,
                    percentReceived: siteTotalExpected > 0
                        ? ((siteTotalReceived / siteTotalExpected) * 100).toFixed(1)
                        : 0,
                    percentPastSLA: siteTotalNYR > 0
                        ? ((siteTotalNYRPastSLA / siteTotalNYR) * 100).toFixed(1)
                        : 0
                }
            })

            // Convert tempZones from object to array format and sort warehouses
            const sortedWarehouses = Object.values(warehouseData).map(warehouse => ({
                ...warehouse,
                warehouseName: warehouse.warehouseId,
                tempZones: Object.entries(warehouse.tempZones).map(([tempZone, data]) => ({
                    tempZone,
                    ...data
                }))
            })).sort((a, b) => a.warehouseId.localeCompare(b.warehouseId))

            // Calculate grand totals across all sites and temp zones
            let grandTotalExpected = 0
            let grandTotalReceived = 0
            let grandTotalNYRPastSLA = 0

            sortedWarehouses.forEach(warehouse => {
                grandTotalExpected += warehouse.siteTotal.totalExpected
                grandTotalReceived += warehouse.siteTotal.totalReceived
                grandTotalNYRPastSLA += warehouse.siteTotal.totalNYRPastSLA
            })

            const grandTotalNYR = Math.max(grandTotalExpected - grandTotalReceived, 0)
            const grandTotals = {
                totalExpected: grandTotalExpected,
                totalReceived: grandTotalReceived,
                totalNYR: grandTotalNYR,
                totalNYRPastSLA: grandTotalNYRPastSLA,
                percentReceived: grandTotalExpected > 0
                    ? ((grandTotalReceived / grandTotalExpected) * 100).toFixed(1)
                    : 0,
                percentPastSLA: grandTotalNYR > 0
                    ? ((grandTotalNYRPastSLA / grandTotalNYR) * 100).toFixed(1)
                    : 0
            }

            return { warehouses: sortedWarehouses, grandTotals }
        }

        return aggregateByTempZone(incomingDockReceived)
    }, [incomingDockReceived, warehouseParams])

    const pagination = usePagination({
        numberOfItemsPerPage: resultsPerPage,
        numberOfResults: summaryData?.warehouses?.length || 0,
    })

    const { visibleIndices } = pagination


    const onChangeResultsPerPage = resultsPerPage => {
        setSelectedResultsPerPage(resultsPerPage)
        if (resultsPerPage === "All") {
            setResultsPerPage(summaryData?.warehouses?.length || 0)
        } else {
            setResultsPerPage(resultsPerPage)
        }
        pagination.onChange(1)
    }

    useEffect(() => {
        onChangeResultsPerPage(selectedResultsPerPage)
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [summaryData])

    const resultsPerPageOptions = [5, 10, 25, "All"]

    return (
        <PageLayout
            loading={!incomingDockReceived}
            width={incomingDockReceived ? "inherit" : undefined}
            alignmentHorizontal="start"
        >
            <Column>
                <Row><Heading level={2}>NYR/NYS Summary by Temp Zone</Heading></Row>
                <Row ref={tableWrapperRef}>
                    <NetworkSLATempSummaryTable
                        summaryData={summaryData}
                        visibleIndices={visibleIndices}
                    />
                </Row>
                {summaryData && summaryData.warehouses && summaryData.warehouses.length > 0 ? (
                    <Row>
                        <TableFooter
                            pagination={pagination}
                            stick={false}
                            resultsPerPage={resultsPerPage}
                            onChangeResultsPerPage={onChangeResultsPerPage}
                            resultsPerPageOptions={resultsPerPageOptions}
                            selectedResultsPerPage={selectedResultsPerPage}
                        />
                    </Row>
                ) : null}
            </Column>
        </PageLayout>
    )
}

NetworkSLATempSummary.propTypes = {
    incomingDockReceived: PropTypes.arrayOf(PropTypes.object),
    warehouseParams: PropTypes.arrayOf(PropTypes.object)
}

export default NetworkSLATempSummary
